import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import {
  ApiError,
  getDeal,
  holdDealReview,
  listManagerDeals,
  releaseDealReviewHold,
  type Deal,
  type DealStatus,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import {
  ManagerDealReview,
  reviewSubtitle,
  reviewTitle,
} from "@/components/deals/ManagerDealReview";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

export interface ManagerDealReviewDrawerProps {
  /** Open when a deal id is set; null closes the drawer. */
  dealId: string | null;
  onClose: () => void;
}

/** Statuses a review hold applies to — mirrors the backend's
 * REVIEWABLE_STATUSES (deal_workflow.hold_review rejects the rest with 409). */
const REVIEWABLE_STATUSES = new Set<DealStatus>([
  "pending_review",
  "extension_pending",
  "win_pending_review",
]);

/** Re-POST the hold well inside the backend's ~30 min TTL so the SLA clock
 * stays paused while the drawer is open (heartbeat). */
const HOLD_HEARTBEAT_MS = 10 * 60_000;

/** Parse the holder's name out of the collision-lock 409 body
 * (`{"detail": "deal is under review by NAME"}`), or null for any other
 * error shape (e.g. the "expected a reviewable status" 409). */
export function holderNameFromError(body: unknown): string | null {
  if (typeof body !== "object" || body === null || !("detail" in body)) return null;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail !== "string") return null;
  const match = /under review by (.+)$/.exec(detail);
  return match?.[1] ?? null;
}

/**
 * Take (and heartbeat) a review hold while a reviewable deal is open in the
 * drawer; best-effort release on close. A collision 409 returns the current
 * holder's name so the drawer can block decision actions.
 */
function useReviewHold(
  accessToken: string | undefined,
  dealId: string | null,
  isReviewable: boolean,
): string | null {
  const [heldBy, setHeldBy] = React.useState<string | null>(null);

  React.useEffect(() => {
    setHeldBy(null);
    if (!accessToken || !dealId || !isReviewable) return;

    let active = true;
    let acquired = false;

    const attempt = async () => {
      try {
        await holdDealReview(accessToken, dealId);
        if (!active) return;
        acquired = true;
        setHeldBy(null);
      } catch (e) {
        if (!active) return;
        if (e instanceof ApiError && e.status === 409) {
          const holder = holderNameFromError(e.body);
          if (holder) setHeldBy(holder);
        }
        // Anything else (network blip, non-collision 409) is best-effort:
        // the hold only pauses SLA sweeps; the review itself still works.
      }
    };

    void attempt();
    const timer = setInterval(() => void attempt(), HOLD_HEARTBEAT_MS);

    return () => {
      active = false;
      clearInterval(timer);
      if (acquired) {
        // Best-effort release — a failure just means the hold expires on its
        // own ~30 min later.
        releaseDealReviewHold(accessToken, dealId).catch(() => {});
      }
    };
  }, [accessToken, dealId, isReviewable]);

  return heldBy;
}

/**
 * Right-hand review drawer for the CM/RA/RM deals ledger (CHP-223) — the
 * primary way managers open a registration / extension / closed-win review,
 * mirroring the partner-side DealDetailDrawer. The standalone
 * `/dashboard/manager/deals/$dealId` route renders the same `ManagerDealReview`
 * for direct links. Decisions hold the toast 1.5s then close the drawer; the
 * ledger refetches (invalidated by the decision) to show the new status.
 *
 * Opening a reviewable deal takes a review hold (pausing the SLA sweeps and
 * collision-locking the deal); the hold heartbeats every ~10 min while the
 * drawer stays open and is released best-effort on close. If another reviewer
 * already holds the deal, a blocking notice names them and decision actions
 * are disabled (read-only detail instead of the adjudication forms).
 */
export function ManagerDealReviewDrawer({ dealId, onClose }: ManagerDealReviewDrawerProps) {
  const { accessToken } = useCurrentUser();

  const deal = useQuery<Deal>({
    queryKey: ["deals", "detail", dealId, accessToken],
    queryFn: () => getDeal(accessToken!, dealId!),
    enabled: !!accessToken && !!dealId,
  });

  // Reuse the ledger row (already cached from the dashboard) for the channel
  // partner + lead names, which the detail response doesn't carry.
  const managerList = useQuery({
    queryKey: ["deals", "manager-list", accessToken],
    queryFn: () => listManagerDeals(accessToken!, "all"),
    enabled: !!accessToken,
  });
  const row = managerList.data?.find((r) => r.id === dealId);

  const d = deal.data;

  const isReviewable = !!d && d.id === dealId && REVIEWABLE_STATUSES.has(d.deal_status);
  const heldBy = useReviewHold(accessToken, dealId, isReviewable);

  return (
    <Sheet
      open={dealId != null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:w-[65vw] sm:max-w-[65vw]"
        data-testid="manager-deal-review-drawer"
      >
        <SheetHeader>
          <SheetTitle>{d ? reviewTitle(d) : "Deal review"}</SheetTitle>
          <SheetDescription>
            {deal.isLoading
              ? "Loading deal…"
              : d
                ? reviewSubtitle(d, row?.channel_partner_company, row?.lead_bdm_name)
                : "Couldn't load this deal."}
          </SheetDescription>
        </SheetHeader>

        {heldBy ? (
          <div
            role="alert"
            data-testid="review-hold-blocked"
            className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800"
          >
            <p className="font-semibold">Under Review by {heldBy}</p>
            <p>
              Decision actions are disabled while they hold this deal. The hold lifts when they
              finish or it expires.
            </p>
          </div>
        ) : null}

        <div className="mt-4">
          {deal.isLoading ? (
            <p role="status">Loading…</p>
          ) : deal.error ? (
            // CHP-804: give the failed-fetch state a retry affordance instead
            // of a dead-end "Couldn't load this deal." line.
            <div role="alert" className="text-sm text-destructive">
              <p>Couldn&apos;t load this deal. Please try again.</p>
              <button
                type="button"
                onClick={() => void deal.refetch()}
                className="mt-1 text-sm font-medium underline underline-offset-2"
              >
                Retry
              </button>
            </div>
          ) : d ? (
            <ManagerDealReview
              deal={d}
              partnerName={row?.channel_partner_company ?? null}
              leadName={row?.lead_bdm_name ?? null}
              onDone={onClose}
              decisionsLocked={!!heldBy}
            />
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
